"use client";

import GenericFacetValue from "@/app/search/facets/genericFacetValue";
import TextInputField from "@/components/design-system/textInputField";
import CaretRightIcon from "public/next-public/common/icons/caret-regular-right.svg";
import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import style from "./genericFacet.module.scss";

export default function GenericFacet({
  displayFacetName,
  facetName,
  startingFacetValues,
  handleChange,
  selectedValues,
  unselectTrigger,
}: GenericFacetProps) {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue] = useDebounce(searchValue, 100);
  const [facetValues, setFacetValues] = useState(startingFacetValues);
  const [numberVisibleItems, setNumberVisibleItems] = useState(5);
  const [visibleState, setVisibleState] = useState<"more" | "less">("less");

  useEffect(() => {
    if (debouncedSearchValue) {
      const filteredFacets = Object.entries(startingFacetValues).filter((facet) => {
        const facetName = facet[0];
        if (facetName.toLocaleLowerCase().includes(debouncedSearchValue.toLocaleLowerCase())) return true;
      });
      const facetsToShow: Record<string, number> = {};
      for (const facet of filteredFacets) {
        const facetname: string = facet[0];
        facetsToShow[facetname] = facet[1];
      }
      setFacetValues(facetsToShow);
    }
  }, [debouncedSearchValue, startingFacetValues]);

  useEffect(() => {
    if (!debouncedSearchValue) {
      setFacetValues(startingFacetValues);
      setVisibleState("less");
      setNumberVisibleItems(5);
    }
  }, [startingFacetValues, debouncedSearchValue]);

  const handleShowMoreLess = (length?: number) => {
    if (length) {
      setVisibleState("more");
      setNumberVisibleItems(length);
    } else {
      setVisibleState("less");
      setNumberVisibleItems(5);
    }
  };

  return (
    <div className={style.facet}>
      <div className={style.name}>{displayFacetName}</div>
      {(Object.entries(facetValues).length > 5 || (searchValue !== "" && Object.entries(facetValues).length <= 5)) && (
        <div className={style.facetSearch}>
          <TextInputField
            name={`search-facet-${facetName}`}
            placeholder=""
            helperText=""
            showHelperText={false}
            showLabel={false}
            showRightIcon={searchValue.length > 0}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onClear={() => {
              setSearchValue("");
              setFacetValues(startingFacetValues);
              setVisibleState("less");
              setNumberVisibleItems(5);
            }}
          />
        </div>
      )}
      <ul className={style.facetList}>
        {Object.entries(facetValues)
          .slice(0, numberVisibleItems)
          .map(([value, count]) => {
            return (
              <GenericFacetValue
                key={value}
                parentFacet={facetName}
                value={value}
                count={count}
                searchTerm={searchValue}
                handleChange={handleChange}
                unselectTrigger={unselectTrigger}
              />
            );
          })}
        {Object.entries(facetValues).length > 5 && (
          <>
            {visibleState === "less" && (
              <div
                onClick={() => handleShowMoreLess(Object.entries(facetValues).length)}
                className={style.showMoreLess}
              >
                Show More
                <CaretRightIcon width={16} height={16} style={{ transform: "rotate(90deg)" }} />
              </div>
            )}
            {visibleState === "more" && (
              <div onClick={() => handleShowMoreLess()} className={style.showMoreLess}>
                Show Less
                <CaretRightIcon width={16} height={16} style={{ transform: "rotate(-90deg)" }} />
              </div>
            )}
          </>
        )}
        {Object.entries(facetValues).length == 0 && <div>No results.</div>}
      </ul>
    </div>
  );
}

export type GenericFacetProps = {
  displayFacetName: string;
  facetName: string;
  /** Facet values from page load, can be refined from a facet search in the component. */
  startingFacetValues: Record<string, number>;
  handleChange?: (event: React.SyntheticEvent, value: string) => void;
  selectedValues: Record<string, Record<string, boolean>>;
  unselectTrigger: number;
};
